/**
 * Utility functions for handling API validation errors
 */

export interface ValidationError {
	title?: string;
	status?: number;
	detail?: string;
	errors?: Record<string, string[]>;
	TraceId?: string;
}

/**
 * Parses API validation errors from 400 responses
 * @param error The error object from the API call
 * @returns Parsed validation error or null if not a validation error
 */
export const parseValidationError = (error: any): ValidationError | null => {
	// Handle ApiException (generated API client)
	if (error?.status === 400 && error?.response) {
		try {
			const errorData = typeof error.response === 'string' ? JSON.parse(error.response) : error.response;
			return errorData;
		} catch {
			// If parsing fails, return basic validation error
			return {
				status: 400,
				title: 'Validation error',
				detail: error.message || 'Validation failed',
			};
		}
	}

	// Handle standard axios-style errors
	if (error?.response?.status === 400) {
		try {
			const errorData = typeof error.response.data === 'string' ? JSON.parse(error.response.data) : error.response.data;
			return errorData;
		} catch {
			// If parsing fails, try to extract from error message
			const message = error.message || error.toString();
			if (message.includes('400')) {
				return {
					status: 400,
					title: 'Validation error',
					detail: message,
				};
			}
		}
	}
	return null;
};

/**
 * Gets a user-friendly error message from a validation error
 * @param validationError The parsed validation error
 * @param fieldName Optional field name to get specific field error
 * @returns User-friendly error message
 */
export const getValidationErrorMessage = (validationError: ValidationError, fieldName?: string): string => {
	if (fieldName && validationError.errors?.[fieldName]) {
		return validationError.errors[fieldName][0];
	}

	return validationError.detail || validationError.title || 'Validation failed. Please check your input and try again.';
};

/**
 * Gets field-specific validation errors
 * @param validationError The parsed validation error
 * @param fieldName The field name to get errors for
 * @returns Array of field-specific error messages
 */
export const getFieldValidationErrors = (validationError: ValidationError, fieldName: string): string[] => {
	return validationError.errors?.[fieldName] || [];
};
